/**
 * Analytics service — abstraction layer over PostHog.
 *
 * All tracking calls go through this module so the provider
 * can be swapped without touching any view code.
 *
 * When PostHog is not configured (no API key), calls are no-ops
 * so the app works fine in dev/test without a tracking backend.
 *
 * Every event includes an "environment" property (development | production)
 * so you can filter dashboards to prod-only. For strict separation, use
 * two PostHog projects: one key in .env for dev, another in Vercel for prod.
 */

import { logger } from "../utils/logger";

let _posthog = null;
let _initialized = false;

/** development | production — from Vite MODE or VITE_APP_ENV override */
export function getEnvironment() {
  const override = import.meta.env.VITE_APP_ENV;
  if (override === "production" || override === "development") return override;
  return import.meta.env.DEV ? "development" : "production";
}

const _envProp = { environment: getEnvironment() };

export function initAnalytics() {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";

  if (!key) {
    if (import.meta.env.DEV) console.info("[analytics] No POSTHOG_KEY — tracking disabled");
    return;
  }

  import("posthog-js").then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: host,
      capture_pageview: false,
      capture_pageleave: true,
      persistence: "localStorage",
      autocapture: false,
      person_profiles: "identified_only",
    });
    _posthog = posthog;
    _initialized = true;
  }).catch(() => {
    logger.warn("[analytics] PostHog failed to load");
  });
}

export function identify(userId, properties = {}) {
  if (!_posthog) return;
  _posthog.identify(String(userId), { ..._envProp, ...properties });
}

export function track(event, properties = {}) {
  if (!_posthog) return;
  _posthog.capture(event, { ..._envProp, ...properties });
}

export function page(pageName, properties = {}) {
  if (!_posthog) return;
  _posthog.capture("$pageview", { $current_url: pageName, ..._envProp, ...properties });
}

export function reset() {
  if (!_posthog) return;
  _posthog.reset();
}

export function isInitialized() {
  return _initialized;
}

// ── Standardized event names ──

export const EVENTS = {
  // App lifecycle
  APP_LOADED: "app_loaded",
  APP_RESUMED: "app_resumed",

  // Auth (Supabase)
  AUTH_LOGIN_EMAIL: "auth_login_email",
  AUTH_LOGIN_GOOGLE: "auth_login_google",
  AUTH_SIGNUP: "auth_signup",
  AUTH_DEMO_LOGIN: "auth_demo_login",
  ADMIN_VIEW_OPENED: "admin_view_opened",
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // Navigation
  PAGE_VIEWED: "page_viewed",

  // Client management
  CLIENT_CREATED: "client_created",
  CLIENT_ARCHIVED: "client_archived",
  CLIENT_REACTIVATED: "client_reactivated",
  CLIENT_STANDBY: "client_standby",
  CLIENT_PROFILE_VIEWED: "client_profile_viewed",
  CLIENT_PROFILE_EDITED: "client_profile_edited",
  CLIENT_TAG_CHANGED: "client_tag_changed",
  COACH_NOTE_ADDED: "coach_note_added",
  SESSION_NOTES_UPDATED: "session_notes_updated",

  // Session lifecycle
  SESSION_STARTED: "session_started",
  SESSION_COMPLETED: "session_completed",
  SESSION_CANCELLED: "session_cancelled",
  EXERCISE_ADDED: "exercise_added",
  EXERCISE_AUTOCOMPLETE_USED: "exercise_autocomplete_used",

  // AI
  AI_SESSION_GENERATED: "ai_session_generated",
  AI_SESSION_REGENERATED: "ai_session_regenerated",
  AI_SESSION_GENERATION_FAILED: "ai_session_generation_failed",
  AI_ALTERNATIVES_REQUESTED: "ai_alternatives_requested",
  AI_ALTERNATIVE_APPLIED: "ai_alternative_applied",
  AI_EXERCISE_REMOVED: "ai_exercise_removed",
  AI_EXERCISE_REORDERED: "ai_exercise_reordered",
  AI_SUMMARY_GENERATED: "ai_summary_generated",
  AI_VOICE_EXTRACTION: "ai_voice_extraction",
  AI_ERROR: "ai_error",
  AI_WORKOUT_GENERATED: "ai_workout_generated",
  AI_WORKOUT_GEN_FAILED: "ai_workout_gen_failed",
  AI_WORKOUT_BLOCK_REGENERATED: "ai_workout_block_regenerated",
  AI_WORKOUT_EXERCISES_VARIED: "ai_workout_exercises_varied",
  AI_WORKOUT_SUGGESTION_USED: "ai_workout_suggestion_used",
  AI_WORKOUT_GATED: "ai_workout_gated",

  // Workouts (builder 1a-alpha)
  WORKOUTS_LIST_VIEWED: "workouts_list_viewed",
  WORKOUT_CREATED: "workout_created",
  WORKOUT_BUILDER_OPENED: "workout_builder_opened",
  WORKOUT_UPDATED: "workout_updated",
  WORKOUT_SAVED_AS_TEMPLATE: "workout_saved_as_template",
  WORKOUT_CREATED_FROM_TEMPLATE: "workout_created_from_template",
  WORKOUT_DUPLICATED: "workout_duplicated",
  WORKOUT_ATTACHED_TO_EVENT: "workout_attached_to_event",
  WORKOUT_LIVE_STARTED: "workout_live_started",
  WORKOUT_LIVE_COMPLETED: "workout_live_completed",
  WORKOUT_EXERCISE_LOGGED: "workout_exercise_logged",
  WORKOUT_NOTES_SAVED: "workout_notes_saved",
  WORKOUT_NOTES_SKIPPED: "workout_notes_skipped",
  WORKOUT_REST_TIMER_USED: "workout_rest_timer_used",

  // Calendar
  EVENT_CANCELLED: "event_cancelled",
  /** Séance passée marquée comme réalisée via ConfirmSessionPopup */
  EVENT_CONFIRMED_DONE: "event_confirmed_done",
  /** Séance passée marquée comme non réalisée via ConfirmSessionPopup (isDue: boolean) */
  EVENT_CONFIRMED_CANCELLED: "event_confirmed_cancelled",
  /** Séances passées en standby (gel) depuis la modale profil — pas une annulation définitive */
  STANDBY_EVENTS_APPLIED: "standby_events_applied",
  /** Séances standby repassées en scheduled à la réactivation client */
  STANDBY_EVENTS_RESUMED: "standby_events_resumed",
  EVENT_UPDATED: "event_updated",
  CALENDAR_WEEK_CHANGED: "calendar_week_changed",
  CALENDAR_NEW_SESSION_CLICKED: "calendar_new_session_clicked",

  // Billing / Finances
  INVOICE_STATUS_TOGGLED: "invoice_status_toggled",
  URSSAF_TOGGLED: "urssaf_toggled",
  FINANCES_REVENUE_VIEWED: "finances_revenue_viewed",
  COACH_CHARGE_ADDED: "coach_charge_added",
  /** @type {string} payload: { action: 'add'|'edit'|'delete', source?: string } */
  FINANCES_CHARGE_MUTATION: "finances_charge_mutation",
  PAYMENTS_RECAP_PDF_EXPORTED: "payments_recap_pdf_exported",
  PAYMENTS_MARKED_PAID: "payments_marked_paid",
  PAYMENTS_BULK_MARKED_PAID: "payments_bulk_marked_paid",

  /** Feature gate / trial (UpgradeTeaser, etc.) */
  FEATURE_USED: "feature_used",

  // Settings
  SETTINGS_SAVED: "settings_saved",
  OFFER_CREATED: "offer_created",
  OFFER_DEACTIVATED: "offer_deactivated",
  TARIF_UPDATED: "tarif_updated",
  THEME_CHANGED: "theme_changed",
  EQUIPMENT_CHANGED: "equipment_changed",

  // Demo
  DEMO_MODE_TOGGLED: "demo_mode_toggled",
};
